import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { projectsApi, cabinetsApi } from "@/lib/api";
import {
  formatCurrency, ITEM_LABELS, ITEM_UNITS, CABINET_CATEGORIES, CABINET_GRADE_TEMPLATES,
  getDefaultCabinetCoreItems,
} from "@/lib/utils";

export default function CabinetEditor() {
  const { projectId, cabinetId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [cabinet, setCabinet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [projectId, cabinetId]);

  const loadData = async () => {
    try {
      const projectRes = await projectsApi.getById(projectId);
      setProject(projectRes.data);
      const foundCabinet = projectRes.data.cabinet_areas.find(c => c.id === cabinetId);
      if (!foundCabinet) { toast.error("Cabinet area not found"); navigate(`/project/${projectId}`); return; }
      setCabinet(foundCabinet);
      const estimateRes = await projectsApi.getEstimate(projectId);
      setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load cabinet area");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePricingModeChange = useCallback((mode) => {
    setCabinet(prev => ({ ...prev, pricing_mode: mode, core_items: getDefaultCabinetCoreItems(mode) }));
  }, []);

  const handleFinishGradeChange = useCallback((grade) => {
    // Only flip which finish-checklist rows are enabled - never regenerate
    // the items list, so edits to logistics/extras/other-grade quantities
    // survive a grade switch (both grades' rows are always present).
    const gradeItems = CABINET_GRADE_TEMPLATES.find(t => t.value === grade)?.items || [];
    const allFinishTypes = CABINET_GRADE_TEMPLATES.flatMap(t => t.items);
    setCabinet(prev => ({
      ...prev,
      finish_grade: grade,
      items: prev.items.map(item => allFinishTypes.includes(item.item_type)
        ? { ...item, enabled: gradeItems.includes(item.item_type) }
        : item),
    }));
  }, []);

  const handleCoreItemChange = useCallback((itemType, field, value) => {
    setCabinet(prev => ({
      ...prev,
      core_items: prev.core_items.map(item => item.item_type === itemType ? { ...item, [field]: value } : item),
    }));
  }, []);

  const handleItemChange = useCallback((itemType, field, value) => {
    setCabinet(prev => ({
      ...prev,
      items: prev.items.map(item => item.item_type === itemType ? { ...item, [field]: value } : item),
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await cabinetsApi.update(projectId, cabinetId, {
        name: cabinet.name, pricing_mode: cabinet.pricing_mode, finish_grade: cabinet.finish_grade,
        core_items: cabinet.core_items, items: cabinet.items, notes: cabinet.notes || "",
      });
      toast.success("Cabinet area saved");
      loadData();
    } catch (error) {
      toast.error("Failed to save cabinet area");
    } finally {
      setSaving(false);
    }
  };

  const getCabinetEstimate = () => {
    if (!estimate?.cabinet_breakdowns) return null;
    return estimate.cabinet_breakdowns.find(cb => cb.cabinet_id === cabinetId);
  };

  const getCoreBreakdown = (itemType) => {
    const cabinetEstimate = getCabinetEstimate();
    return cabinetEstimate?.core_breakdowns?.find(b => b.item_type === itemType);
  };

  const renderCoreItems = () => {
    return (
      <div className="space-y-3">
        {cabinet.core_items.map((item) => {
          const breakdown = getCoreBreakdown(item.item_type);
          return (
            <div key={item.item_type} className="p-4 border border-amber-500/50 bg-card rounded-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-sm">{ITEM_LABELS[item.item_type] || item.item_type}</span>
                {breakdown && (
                  <span className="text-xs px-2 py-1 rounded-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 font-mono">
                    {formatCurrency(breakdown.rate)}/{ITEM_UNITS[item.item_type] || "unit"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="input-group">
                  <Label className="input-label text-xs">Qty ({ITEM_UNITS[item.item_type] || "units"})</Label>
                  <Input type="number" value={item.quantity} onChange={(e) => handleCoreItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9" data-testid={`core-quantity-${item.item_type}`} />
                </div>
                {breakdown && (
                  <div className="input-group">
                    <Label className="input-label text-xs">Price</Label>
                    <div className="h-9 flex items-center font-mono text-sm">{formatCurrency(breakdown.price)}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Logistics/extras/finish-checklist items reuse the switch-card chrome but
  // with a lean body: just an hours quantity, since none of coats/paint-
  // type/prep-level/needs_primer apply to hours-tracked cabinet work.
  const renderHoursItemGroup = (categoryName, itemTypes, skipHeading = false) => {
    const categoryItems = cabinet.items?.filter(item => itemTypes.includes(item.item_type)) || [];
    if (categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="space-y-3">
        {!skipHeading && <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2">{categoryName}</h3>}
        {categoryItems.map((item) => (
          <div key={item.item_type} className={`p-4 border rounded-sm transition-colors ${item.enabled ? 'border-amber-500/50 bg-card' : 'border-border bg-muted/20'}`} data-testid={`item-${item.item_type}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Switch checked={item.enabled} onCheckedChange={(checked) => handleItemChange(item.item_type, 'enabled', checked)} data-testid={`toggle-${item.item_type}`} />
                <span className="font-medium text-sm">{ITEM_LABELS[item.item_type] || item.item_type}</span>
              </div>
              {item.enabled ? (
                <span className="text-xs px-2 py-1 rounded-sm bg-green-500/20 text-green-400 border border-green-500/30"><Check className="w-3 h-3 inline mr-1" />Included</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border"><X className="w-3 h-3 inline mr-1" />Excluded</span>
              )}
            </div>
            {item.enabled && (
              <div className="input-group">
                <Label className="input-label text-xs">Hours</Label>
                <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9 max-w-[10rem]" data-testid={`quantity-${item.item_type}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading cabinet area...</div></div>;
  if (!cabinet || !project) return null;

  const cabinetEstimate = getCabinetEstimate();
  const finishCategoryKey = cabinet.finish_grade === "stain_grade" ? "finish_stain_grade" : "finish_paint_grade";
  const finishSectionLabel = cabinet.finish_grade === "stain_grade" ? "Stain-Grade Finish Process" : "Paint-Grade Finish Process";
  const moreItemsDefaultOpen = [
    ["Logistics & Setup", CABINET_CATEGORIES.logistics],
    ["Extras", CABINET_CATEGORIES.extras],
    [finishSectionLabel, CABINET_CATEGORIES[finishCategoryKey]],
  ].filter(([, itemTypes]) => cabinet.items?.some(i => itemTypes.includes(i.item_type) && i.enabled))
   .map(([label]) => label);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}`)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">{cabinet.name}</h1>
              <p className="text-xs text-muted-foreground">{project.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="save-cabinet-btn"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Cabinet Area"}</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Cabinet Area Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group"><Label className="input-label">Area Name</Label><Input value={cabinet.name} onChange={(e) => setCabinet({ ...cabinet, name: e.target.value })} data-testid="cabinet-name-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <Label className="input-label">Pricing Mode</Label>
                    <Select value={cabinet.pricing_mode} onValueChange={handlePricingModeChange}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linear_foot">Linear Foot</SelectItem>
                        <SelectItem value="per_unit">Per Unit (Door/Drawer/Box)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="input-group">
                    <Label className="input-label">Finish Grade</Label>
                    <Select value={cabinet.finish_grade} onValueChange={handleFinishGradeChange}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CABINET_GRADE_TEMPLATES.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Core Pricing</CardTitle></CardHeader>
              <CardContent>{renderCoreItems()}</CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Logistics, Extras & Finish Process</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <Accordion type="multiple" defaultValue={moreItemsDefaultOpen} className="space-y-0">
                  {[
                    ["Logistics & Setup", CABINET_CATEGORIES.logistics],
                    ["Extras", CABINET_CATEGORIES.extras],
                    [finishSectionLabel, CABINET_CATEGORIES[finishCategoryKey]],
                  ].map(([label, itemTypes]) => {
                    const enabledCount = cabinet.items?.filter(i => itemTypes.includes(i.item_type) && i.enabled).length || 0;
                    return (
                      <AccordionItem key={label} value={label}>
                        <AccordionTrigger className="text-xs uppercase tracking-widest text-muted-foreground font-medium hover:no-underline">
                          <span>{label}{enabledCount > 0 && <span className="ml-2 text-amber-500 normal-case">({enabledCount} included)</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent>
                          {label === finishSectionLabel && (
                            <p className="text-xs text-muted-foreground mb-3">
                              Tracked for your own scheduling reference - already bundled into the core unit price above, not billed separately.
                            </p>
                          )}
                          {renderHoursItemGroup(label, itemTypes, true)}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Notes</CardTitle></CardHeader>
              <CardContent><Textarea value={cabinet.notes || ""} onChange={(e) => setCabinet({ ...cabinet, notes: e.target.value })} placeholder="Add any special notes..." rows={3} /></CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="sticky-panel">
              <CardHeader><CardTitle className="font-heading text-lg">Cabinet Estimate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {cabinetEstimate ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Tier</span><span className="font-mono text-sm">{cabinetEstimate.tier_label}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Logistics Hours</span><span className="font-mono">{cabinetEstimate.total_hours} hrs</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Logistics Labor</span><span className="font-mono">{formatCurrency(cabinetEstimate.labor_cost)}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Finish Process Hours (unbilled)</span><span className="font-mono text-muted-foreground">{cabinetEstimate.finish_process_hours} hrs</span></div>
                    </div>
                    <div className="border-t border-border pt-4"><div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Core Price</span><span className="font-mono">{formatCurrency(cabinetEstimate.core_price)}</span></div></div>
                    <div className="border-t border-border pt-4"><div className="flex justify-between items-center"><span className="font-heading font-semibold">Area Total</span><span className="font-mono text-xl text-amber-500">{formatCurrency(cabinetEstimate.core_price + cabinetEstimate.labor_cost)}</span></div></div>
                  </>
                ) : (
                  <div className="text-center py-4"><p className="text-muted-foreground text-sm">Save cabinet area to see estimate</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
